import React, { useRef, useState } from 'react';
import { MoreHorizontal, UserPlus, Lock, Cloud, CloudOff, Star, Pencil, Trash2, Loader2 } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import ContextMenu from '../ui/ContextMenu';
import i18n from '../../i18n';
import { locationActionFor } from '../../plans/cloudAccess';

// The click-catching wrapper of a portalled menu takes no place in the row:
// as a real box it joined the row's flex gap and shifted the row by 10 px
// whenever the menu opened (owner, 13.10.2026).
const MENU_WRAP = { display: 'contents' };

/**
 * The board's ⋯ menu on an overview tile and an overview row (owner,
 * 13.10.2026: "I moved the buttons to the ... menu" — BoardsGallery
 * artboard). Replaces the hover icon buttons the stats strip used to reveal.
 *
 * Rows, in order: Share with team · Keep on this device (or Download to this
 * device) / Move to cloud ·
 * Pin / Unpin · Edit board · — · Delete board. The gates are the ones the
 * hover buttons had:
 *   - Share with team: private boards when a team can take it (`canShare`);
 *     a team board offers "Make private" in that slot instead, only when the
 *     caller hands `onMakePrivate` (not a viewer).
 *   - Move: private boards only, when `onMoveLocation` is given; disabled
 *     with a spinner while `moving`.
 *   - Pin / Unpin: whenever `onTogglePin` is given (pins are personal).
 *   - Edit / Delete: `canManage` only, so a team viewer sees neither.
 *
 * The menu is the shared ContextMenu in its keyboard mode (focus moves in,
 * arrows move, Escape returns to ⋯). It is portalled, but React bubbles its
 * clicks through this tree to the tile, which would open the board — hence
 * the wrapper that stops them.
 */
const stop = (e) => e.stopPropagation();

export function boardMenuItems({ board, canManage, canShare, onEdit, onDelete, onShare, onMakePrivate, onMoveLocation, moving, onDevice, pinned, onTogglePin, projectMenu = [] }) {
  const isTeam = board.visibility === 'team';
  const head = [
    !isTeam && canShare && onShare && { key: 'share', icon: <UserPlus size={13} />, label: 'Share with team', onClick: () => onShare(board) },
    isTeam && onMakePrivate && { key: 'private', icon: <Lock size={13} />, label: 'Make private', onClick: () => onMakePrivate(board) },
    !isTeam &&
      onMoveLocation && {
        key: 'move',
        icon: moving ? <Loader2 size={13} className="animate-spin" /> : onDevice ? <Cloud size={13} /> : <CloudOff size={13} />,
        // Plans v2 card 3.4: a cloud board of a plan without cloud sync is
        // downloaded (a copy), never moved out of the cloud.
        label: onDevice
          ? i18n.t('cloudLocal.moveToCloud')
          : locationActionFor('cloud') === 'download'
            ? i18n.t('cloudLocal.downloadToDevice')
            : i18n.t('cloudLocal.moveToDevice'),
        disabled: !!moving,
        onClick: () => onMoveLocation(board),
      },
    onTogglePin && {
      key: 'pin',
      icon: <Star size={13} className="boards-tile-menu-star" fill={pinned ? 'currentColor' : 'none'} />,
      label: pinned ? 'Unpin' : 'Pin',
      onClick: () => onTogglePin(board),
    },
    // Project scope (card 3.1): "Add to project ›" / "Remove from <project>".
    ...(projectMenu || []),
    canManage && onEdit && { key: 'edit', icon: <Pencil size={13} />, label: 'Edit board', onClick: () => onEdit(board) },
  ].filter(Boolean);
  const del = canManage && onDelete ? [{ key: 'delete', icon: <Trash2 size={13} />, label: 'Delete board', danger: true, onClick: () => onDelete(board) }] : [];
  return head.length && del.length ? [...head, { key: 'sep', separator: true }, ...del] : [...head, ...del];
}

export default function BoardTileMenu(props) {
  const { board, moving } = props;
  const ref = useRef(null);
  const [menu, setMenu] = useState(null);
  // Was the menu open when this press began? The menu's outside-mousedown
  // handler closes it before the click lands, so without this a click on ⋯
  // would close and immediately reopen it instead of toggling it shut.
  const wasOpen = useRef(false);
  const items = boardMenuItems(props);
  if (items.length === 0) return null;

  const open = (e) => {
    e.stopPropagation();
    if (wasOpen.current) {
      wasOpen.current = false;
      setMenu(null);
      return;
    }
    const r = ref.current?.getBoundingClientRect();
    setMenu(r ? { x: r.right, y: r.bottom + 4 } : { x: e.clientX, y: e.clientY });
  };

  return (
    <>
      <Tooltip content="Board actions" side="bottom" disabled={!!menu}>
        <button
          ref={ref}
          type="button"
          className={`boards-tile-menu-btn${menu ? ' boards-tile-menu-btn--open' : ''}`}
          aria-label={`Board actions for ${board.name}`}
          aria-haspopup="menu"
          aria-expanded={!!menu}
          aria-busy={moving ? true : undefined}
          onPointerDown={(e) => {
            e.stopPropagation();
            wasOpen.current = !!menu;
          }}
          onClick={open}
        >
          {moving ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <MoreHorizontal size={14} aria-hidden />}
        </button>
      </Tooltip>
      {menu && (
        <span style={MENU_WRAP} onClick={stop} onPointerDown={stop} onMouseDown={stop}>
          <ContextMenu
            x={menu.x}
            y={menu.y}
            align="end"
            keyboard
            label="Board actions"
            className="boards-tile-menu"
            onClose={() => setMenu(null)}
            items={items}
          />
        </span>
      )}
    </>
  );
}
